<template>
  <div class="tiptap-wrapper">
    <!-- Toolbar -->
    <div v-if="editor" class="tiptap-toolbar">
      <button @click="editor.chain().focus().setParagraph().run()" :class="{ active: !editor.isActive('heading') && !editor.isActive('bulletList') && !editor.isActive('taskList') }" title="Normal text">¶</button>
      <span class="sep"></span>
      <button @click="editor.chain().focus().toggleBold().run()" :class="{ active: editor.isActive('bold') }" title="Bold">B</button>
      <button @click="editor.chain().focus().toggleItalic().run()" :class="{ active: editor.isActive('italic') }" title="Italic"><em>I</em></button>
      <span class="sep"></span>
      <button @click="editor.chain().focus().toggleHeading({ level: 1 }).run()" :class="{ active: editor.isActive('heading', { level: 1 }) }" title="Heading 1">H1</button>
      <button @click="editor.chain().focus().toggleHeading({ level: 2 }).run()" :class="{ active: editor.isActive('heading', { level: 2 }) }" title="Heading 2">H2</button>
      <span class="sep"></span>
      <button @click="editor.chain().focus().toggleBulletList().run()" :class="{ active: editor.isActive('bulletList') }" title="Bullet list">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
      </button>
      <button @click="editor.chain().focus().toggleTaskList().run()" :class="{ active: editor.isActive('taskList') }" title="Task list">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/></svg>
      </button>
    </div>

    <!-- Editor -->
    <div @click="focusEditor" class="tiptap-editor-area">
      <editor-content :editor="editor" class="tiptap-content" />
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, watch } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: 'Add description...' },
})

const emit = defineEmits(['update:modelValue', 'blur'])

/** Escape plain text for safe HTML insertion. */
function escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Turn plain text (with newlines) into TipTap-friendly HTML.
 * Blank lines → separate paragraphs; single newlines → <br>.
 */
function plainTextToHtml(text) {
  if (!text) return ''
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split(/\n{2,}/)
    .map((block) => {
      const lines = block.split('\n').map(escapeHtml)
      return `<p>${lines.join('<br>') || '<br>'}</p>`
    })
    .join('')
}

/**
 * Normalize stored description for the editor.
 * Plain-text payloads (no tags) keep their newlines as paragraphs/breaks.
 * HTML that was stored as a single run-on paragraph is left as-is (user can re-paste).
 */
function normalizeContent(val) {
  if (!val) return ''
  // Already looks like HTML markup
  if (/<[a-z][\s\S]*>/i.test(val)) return val
  // Plain text — preserve newlines
  if (val.includes('\n')) return plainTextToHtml(val)
  return `<p>${escapeHtml(val)}</p>`
}

/**
 * If clipboard is plain text only (or HTML is just a single wrapper with no breaks),
 * convert newlines into real paragraphs / hard breaks instead of collapsing them.
 */
function handlePlainTextPaste(_view, event, editor) {
  const clipboard = event.clipboardData
  if (!clipboard) return false

  const text = clipboard.getData('text/plain')
  if (!text || !/\r|\n/.test(text)) return false

  const html = clipboard.getData('text/html') || ''
  // Trust TipTap's default paste when HTML already encodes structure/breaks.
  const blockCloses = (html.match(/<\/(p|div|li|h[1-6]|tr)>/gi) || []).length
  const hasBr = /<br\s*\/?>/i.test(html)
  if (html && (blockCloses > 1 || hasBr)) return false

  event.preventDefault()
  editor.commands.insertContent(plainTextToHtml(text))
  return true
}

const editor = useEditor({
  content: normalizeContent(props.modelValue),
  extensions: [
    StarterKit.configure({
      heading: { levels: [1, 2] },
    }),
    Placeholder.configure({ placeholder: props.placeholder }),
    TaskList,
    TaskItem.configure({ nested: true }),
  ],
  editorProps: {
    handlePaste(view, event) {
      if (!editor.value) return false
      return handlePlainTextPaste(view, event, editor.value)
    },
  },
  onUpdate({ editor: ed }) {
    emit('update:modelValue', ed.getHTML())
  },
  onBlur() {
    emit('blur')
  },
})

watch(() => props.modelValue, (val) => {
  if (!editor.value) return
  const next = normalizeContent(val || '')
  const current = editor.value.getHTML()
  // Skip when the editor already shows this content (raw or normalized)
  if (current === val || current === next) return
  editor.value.commands.setContent(next, false)
})

function focusEditor() {
  editor.value?.commands.focus()
}

onBeforeUnmount(() => {
  editor.value?.destroy()
})
</script>

<style scoped>
.tiptap-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.tiptap-toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 4px;
  flex-shrink: 0;
}

.tiptap-toolbar button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 24px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  background: none;
  border: none;
  border-radius: 3px;
  cursor: pointer;
}

.tiptap-toolbar button:hover {
  background: #f1f5f9;
  color: #374151;
}

.tiptap-toolbar button.active {
  background: #e0f2fe;
  color: #0369a1;
}

.tiptap-toolbar .sep {
  width: 1px;
  height: 16px;
  background: #e5e7eb;
  margin: 0 4px;
}

.tiptap-editor-area {
  flex: 1;
  overflow-y: auto;
  cursor: text;
}

.tiptap-content {
  height: 100%;
}

.tiptap-content :deep(.tiptap) {
  height: 100%;
  min-height: 200px;
  outline: none;
  font-size: 0.875rem;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Visible spacing between blocks so pasted multi-line text reads clearly */
.tiptap-content :deep(.tiptap p) {
  margin: 0 0 8px;
  min-height: 1.25em;
}
.tiptap-content :deep(.tiptap p:last-child) {
  margin-bottom: 0;
}
.tiptap-content :deep(.tiptap h1) { font-size: 1.25rem; font-weight: 600; line-height: 1.3; margin: 12px 0 6px; }
.tiptap-content :deep(.tiptap h2) { font-size: 1.1rem; font-weight: 600; line-height: 1.3; margin: 10px 0 4px; }
.tiptap-content :deep(.tiptap ul) { padding-left: 20px; margin: 6px 0 10px; list-style-type: disc; }
.tiptap-content :deep(.tiptap ol) { padding-left: 20px; margin: 6px 0 10px; list-style-type: decimal; }
.tiptap-content :deep(.tiptap li) { margin: 3px 0; }
.tiptap-content :deep(.tiptap li p) { margin: 0; }
.tiptap-content :deep(.tiptap strong) { font-weight: 600; }
.tiptap-content :deep(.tiptap code) { background: #f1f5f9; padding: 1px 4px; border-radius: 3px; font-size: 0.85em; }
.tiptap-content :deep(.tiptap blockquote) { border-left: 3px solid #d1d5db; padding-left: 10px; margin: 8px 0; color: #6b7280; }
.tiptap-content :deep(.tiptap br) { display: block; content: ""; margin-top: 0; }

/* Task list */
.tiptap-content :deep(.tiptap ul[data-type="taskList"]) {
  list-style: none;
  padding-left: 0;
}

.tiptap-content :deep(.tiptap ul[data-type="taskList"] li) {
  display: flex;
  align-items: flex-start;
  gap: 0.4em;
}

.tiptap-content :deep(.tiptap ul[data-type="taskList"] li label) {
  margin-top: 0.2em;
}

.tiptap-content :deep(.tiptap ul[data-type="taskList"] li[data-checked="true"] > div > p) {
  text-decoration: line-through;
  color: #9ca3af;
}

/* Placeholder */
.tiptap-content :deep(.tiptap p.is-editor-empty:first-child::before) {
  content: attr(data-placeholder);
  float: left;
  color: #9ca3af;
  pointer-events: none;
  height: 0;
}
</style>
